/**
 * Каркас кабинета (client-cabinet-shell): шапка (бренд + организация + пользователь + колокольчик),
 * левая навигация из `me/context.nav.sections` и область контента (Outlet). Навигация и видимость
 * разделов целиком определяются сервером по роли — фронт лишь раскрывает идентификаторы.
 */
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Topbar } from '@/design-system/components/layout/topbar'
import { Sidebar, NavGroup } from '@/design-system/components/layout/sidebar'
import { Divider } from '@/design-system/components/layout/divider'
import { Avatar } from '@/design-system/components/primitives/avatar'
import { CompanyBadge } from '@/design-system/components/primitives/company-badge'
import { Icon } from '@/design-system/components/primitives/icon'
import { WorkScaleProvider } from '@/design-system/work-scale'
import { useAuth } from './auth-context'
import { NavLinkItem } from './navigation'
import { NotificationDropdown } from './notification-bell'
import { OrganizationSwitcher } from './organization-switcher'
import { OnboardingModal } from './onboarding-modal'
import { BELOW_DIVIDER_SECTIONS, isTeamRole, resolveNavItems } from './nav'
import type { Role, User } from '@/lib/api'
import maxLogo from '@/assets/max-logo.svg'

/** Раздел, у которого показывается счётчик заказов, ждущих клиента (centrika-docs!98). */
const ORDERS_SECTION = 'orders'
/** «Вопросы клиентов» команды: счётчик вопросов, ждущих ответа (client-questions). */
const QUESTIONS_SECTION = 'questions'

/** Число вопросов, ждущих ответа Центрики, у пункта «Вопросы клиентов». */
function QuestionsAwaitingTeamCounter({ count }: { count: number }) {
  return (
    <span
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-bg px-1.5 text-12 font-semibold tabular-nums text-red"
      title="Вопросы ждут ответа"
      data-testid="questions-awaiting-team"
    >
      <span aria-hidden="true">{count}</span>
      <span className="sr-only">{`Ждут ответа: ${count}`}</span>
    </span>
  )
}

/**
 * Счётчик заказов, ждущих действий клиента (centrika-docs!98, training-order-filling): «На
 * заполнении» и «Требуются правки» по всем заказам клиента (`nav.ordersAwaitingClient`, считает
 * сервер). Число справа от подписи «Мои заказы», видно на любой странице кабинета, в том числе при
 * активном пункте; для чтения с экрана — полная фраза.
 */
function OrdersAwaitingClientCounter({ count }: { count: number }) {
  return (
    <span
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-bg px-1.5 text-12 font-semibold tabular-nums text-red"
      title="Заказы ждут ваших действий"
      data-testid="orders-awaiting-client"
    >
      <span aria-hidden="true">{count}</span>
      <span className="sr-only">{`Ждут ваших действий: ${count}`}</span>
    </span>
  )
}

const ROLE_LABEL: Record<Role, string> = {
  admin: 'Администратор',
  head: 'Руководитель',
  employee: 'Сотрудник',
  company: 'Представитель организации',
  individual: 'Личный кабинет',
}

function deriveCompanyInitials(name: string): string {
  const cleaned = name.replace(/[«»"().]/g, ' ').replace(/\b(ООО|АО|ЗАО|ПАО|ИП|ОАО)\b/gi, ' ')
  const words = cleaned.split(/\s+/).filter(Boolean)
  const letters = words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '')
  return letters.join('') || name.slice(0, 2).toUpperCase()
}

/** Блок организации/контекста в шапке — зависит от стороны роли. */
function HeaderContext({ user }: { user: User }) {
  if (user.org) {
    const org = (
      <>
        <CompanyBadge initials={deriveCompanyInitials(user.org.name)} company={user.org.name} />
        <div>
          <div className="text-13 font-semibold text-txt">{user.org.name}</div>
          <div className="text-12 text-txt-tertiary">ваш личный кабинет</div>
        </div>
      </>
    )
    // За входом несколько организаций — название организации открывает их список (multi-organization-login).
    return (user.organizationsCount ?? 0) > 1 ? <OrganizationSwitcher>{org}</OrganizationSwitcher> : org
  }
  const caption = isTeamRole(user.role) ? 'портал команды' : 'личный кабинет'
  return (
    <div>
      <div className="text-13 font-semibold text-txt">Центрика</div>
      <div className="text-12 text-txt-tertiary">{caption}</div>
    </div>
  )
}

function HeaderUser({ user }: { user: User }) {
  // Представитель организации у входа (login-representative; ТЗ п.9.4): его ФИО и должность;
  // должности нет — «Представитель организации». Без представителя — как раньше.
  const rep = user.role === 'company' ? user.representative : undefined
  const name = rep ? rep.fullName : user.fullName
  const position = rep ? rep.position || ROLE_LABEL.company : user.position || ROLE_LABEL[user.role]
  return (
    <div className="flex items-center gap-2.5">
      <div className="text-right">
        <div className="text-13 font-medium text-txt">{name}</div>
        {position ? <div className="text-12 text-txt-secondary">{position}</div> : null}
      </div>
      <Avatar name={name} />
    </div>
  )
}

/**
 * Канал Центрики в мессенджере MAX (ТЗ заказчицы 26.10.2026, п.9.6; ответ заказчицы 04.11): плашка
 * в рамке фирменного цвета, по центру — значок MAX и текст заказчицы; ссылка — вся плашка,
 * открывается в новой вкладке. Значок — официальный (max.ru/favicon.svg).
 */
const MAX_CHANNEL_URL = 'https://max.ru/id2309154627_biz'
export const MAX_CHANNEL_TEXT =
  'Подписывайтесь на наш интересный и полезный канал о производственной безопасности в MAX'

/** Плашка канала в MAX под разделами левого меню — только клиентам (компания и физлицо). */
function MaxChannelLink() {
  return (
    <a
      href={MAX_CHANNEL_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="ds-focus-ring mx-2 mt-3 flex flex-col items-center gap-2 rounded-12 border-2 border-brand-blue bg-brand-blue-bg p-3 text-center text-12 leading-snug text-txt transition-colors hover:bg-panel"
    >
      <img src={maxLogo} alt="" aria-hidden className="size-8" />
      <span>{MAX_CHANNEL_TEXT}</span>
    </a>
  )
}

export function CabinetLayout() {
  const { context, refresh, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [bellOpen, setBellOpen] = useState(false)
  // Скрыть модалку онбординга сразу после действия (пропустить/заполнить), не дожидаясь
  // круговорота refresh() — избегает мигания. refresh() всё равно обновит onboarding
  // из сессии, так что при следующем логине/маршруте состояние снова верное.
  // Тот же флаг закрывает модалку и по явному «Закрыть»/Escape (замечание РП №2,
  // p2-remarks-2, OnboardingModal.onDismiss) — без обращения к серверу: флаг локален для
  // этого маунта CabinetLayout, так что при следующем входе (новый маунт) модалка,
  // если профиль всё ещё неполон, покажется снова — закрытие транзиентно.
  const [onboardingDismissed, setOnboardingDismissed] = useState(false)

  // Вернулись на вкладку браузера — перечитать контекст кабинета: точки на колокольчике и
  // конвертике (ответ на вопрос, client-questions) и счётчики меню не ждут перезагрузки страницы.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  // RequireAuth гарантирует наличие контекста на этом маршруте.
  if (!context) return null
  const { user, nav, bell, onboarding } = context
  const showOnboarding = Boolean(
    onboarding && !onboarding.profileComplete && !onboarding.skipped && !onboardingDismissed,
  )

  const items = resolveNavItems(user.role, nav.sections)
  const mainItems = items.filter((i) => !BELOW_DIVIDER_SECTIONS.has(i.section))
  const belowDividerItems = items.filter((i) => BELOW_DIVIDER_SECTIONS.has(i.section))
  const team = isTeamRole(user.role)
  const ordersAwaitingClient = team ? 0 : (nav.ordersAwaitingClient ?? 0)
  const questionsAwaitingTeam = team ? (nav.questionsAwaitingTeam ?? 0) : 0
  // Конвертик — у клиента с разделом «Мои вопросы» (client-questions; ТЗ п.9.7).
  const hasQuestions = !team && nav.sections.includes(QUESTIONS_SECTION)

  /**
   * Счётчик справа от подписи пункта: у «Мои заказы» клиента — заказы, ждущие его действий; у
   * «Вопросов клиентов» команды — вопросы, ждущие ответа.
   */
  function trailingFor(section: string) {
    if (section === ORDERS_SECTION && ordersAwaitingClient > 0) {
      return <OrdersAwaitingClientCounter count={ordersAwaitingClient} />
    }
    if (section === QUESTIONS_SECTION && questionsAwaitingTeam > 0) {
      return <QuestionsAwaitingTeamCounter count={questionsAwaitingTeam} />
    }
    return undefined
  }

  function isActive(path: string): boolean {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  return (
    // WorkScaleProvider — объявляет, что здесь действует шкала рабочей области (centrika-docs!31).
    // Обёрнут весь кабинет, а не только `<main>`: диалоги (в т.ч. модалка онбординга, которую
    // открывает сам каркас) рендерятся порталом в `document.body` и берут шкалу из контекста, а
    // не по DOM-наследованию. Страницы входа/регистрации провайдера не имеют — там базовая шкала.
    <WorkScaleProvider>
      <div className="flex min-h-screen flex-col bg-bg">
        {showOnboarding ? (
          <OnboardingModal
            onSkip={() => {
              setOnboardingDismissed(true)
              void refresh()
            }}
            onComplete={() => {
              setOnboardingDismissed(true)
              void refresh()
            }}
            onDismiss={() => setOnboardingDismissed(true)}
          />
        ) : null}
        <div className="relative">
          <Topbar
            company={<HeaderContext user={user} />}
            user={<HeaderUser user={user} />}
            hasUnread={bell.unread > 0}
            onBellClick={() => setBellOpen((o) => !o)}
            onMessagesClick={hasQuestions ? () => navigate('/questions') : undefined}
            hasUnreadMessages={(nav.questionsUnread ?? 0) > 0}
            onLogout={() => void logout()}
          />
          {bellOpen ? (
            <NotificationDropdown
              onClose={() => setBellOpen(false)}
              onChanged={() => void refresh()}
            />
          ) : null}
        </div>

        <div className="flex flex-1">
          <Sidebar>
            {mainItems.map((item) =>
              item.children && item.children.length > 0 ? (
                <NavGroup
                  key={item.section}
                  parent={
                    // Родитель группы подсвечивается на своих страницах, кроме страниц подпунктов:
                    // подразделы «Экологии» лежат под её путём (`/ecology/projects`) и подсвечиваются
                    // сами, а опрос (`/ecology/survey`) — страница самой «Экологии».
                    <NavLinkItem
                      to={item.path}
                      active={
                        isActive(item.path) && !item.children.some((child) => isActive(child.path))
                      }
                      icon={<Icon as={item.icon} />}
                    >
                      {item.label}
                    </NavLinkItem>
                  }
                >
                  {item.children.map((child) => (
                    <NavLinkItem
                      key={child.section}
                      to={child.path}
                      active={isActive(child.path)}
                      icon={<Icon as={child.icon} />}
                      wrap
                    >
                      {child.label}
                    </NavLinkItem>
                  ))}
                </NavGroup>
              ) : (
                <NavLinkItem
                  key={item.section}
                  to={item.path}
                  active={isActive(item.path)}
                  icon={<Icon as={item.icon} />}
                  trailing={trailingFor(item.section)}
                  // Подпись со счётчиком («Вопросы клиентов» и число) переносится, а не обрезается.
                  wrap={trailingFor(item.section) !== undefined}
                >
                  {item.label}
                </NavLinkItem>
              ),
            )}
            {belowDividerItems.length > 0 ? (
              <>
                <Divider />
                {belowDividerItems.map((item) => (
                  <NavLinkItem
                    key={item.section}
                    to={item.path}
                    active={isActive(item.path)}
                    icon={<Icon as={item.icon} />}
                  >
                    {item.label}
                  </NavLinkItem>
                ))}
              </>
            ) : null}
            {isTeamRole(user.role) ? null : <MaxChannelLink />}
          </Sidebar>

          {/* ds-work-scale — рабочая область: увеличенная на 2 px шкала шрифтов (centrika-docs!31).
            Шапка и левое меню остаются на базовой шкале, поэтому класс висит именно здесь. */}
          <main className="ds-work-scale min-w-0 flex-1 p-6 nw:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </WorkScaleProvider>
  )
}
